import Security from "@mui/icons-material/Security";
import { Box, Grid } from "@mui/material";
import MainProductCard from "components/Cards/MainProductCard";
import ListingContainer from "components/UI/organisms/ListingContainer";
import { COMPONENT_TYPES } from "helpers/utils/constants";
import React from "react";
import { isTrue } from "helpers/utils/validators";
import { challenge } from "assets/svg/green";
import { resource } from "assets/svg/purple";
import { lab } from "assets/svg/blue";
import { skills as skillImg } from "assets/svg/yellow";
import ChipsPreview from "components/UI/atoms/ChipsPreview";
import useCareers from "store/careers/service-hook";
import { loginToPreceed, payloadBool } from "helpers/utils/utilities";
import Button from "components/Button";
import useRedirect from "helpers/hooks/useRedirect";
import { REDUCER_TYPES } from "store";
import { useSelector } from "react-redux";
import EmptyText from "components/UI/atoms/EmptyText";

const CareersListing = ({
  gridType,
  list = [],
  setList = Function.prototype,
  loading,
  page = 1,
  setPage = Function.prototype,
  pageCount = 1,
  t,
  searchVal,
  showAll = Function.prototype,
  fromRecommendations,
  isSkills,
}) => {
  const { save, unsave } = useCareers();
  const { navigateProfile } = useRedirect();
  const { isLoggedIn, user } = useSelector(
    (state) => state[REDUCER_TYPES.AUTH]
  );
  const handleSave = async (id, isSaved) => {
    if (!isLoggedIn) {
      loginToPreceed(t);
      return;
    }
    const alreadySaved = isTrue(isSaved);
    const updatedList = list.map((d) => ({
      ...d,
      is_saved: d?.id === id ? payloadBool(!alreadySaved) : d?.is_saved,
    }));
    setList([...updatedList]);
    alreadySaved ? await unsave(id, isSkills) : await save(id, isSkills);
  };

  return (
    <ListingContainer
      loading={loading}
      isEmpty={!list?.length}
      count={pageCount}
      page={page}
      setPage={setPage}
      classes="mt-4"
      emptyView={
        searchVal ? (
          <EmptyText>{t("No skills found!")}</EmptyText>
        ) : (
          <Box className="h-250 flex-column items-center justify-center px-4">
            <p className="text-center fs-14">
              {t(
                "These recommendations are fine-tuned based on the skills you've added to your profile. The more you enrich your profile with additional skills, the more accurate and relevant the results become."
              )}
            </p>
            <Button
              classes="mt-6"
              onClick={() =>
                navigateProfile(user?.username, isSkills ? 1 : null)
              }
            >
              {isSkills ? t("Add Your Skills") : t("Add Your Work Experience")}
            </Button>
          </Box>
        )
      }
      noPagination={fromRecommendations}
    >
      {list?.map((d, i) => (
        <Grid item key={i} xs={gridType === 1 ? 12 : 4}>
          <MainProductCard
            classes="mb-3"
            title={
              <div className="flex items-center">
                <p className="text-nowrap overflow-hidden maxw-85p textOverflow-ellipsis">
                  {d?.title}
                </p>
                <Security
                  fontSize="small"
                  style={{
                    visibility: isTrue(d?.privacy) ? "visible" : "hidden",
                  }}
                />
              </div>
            }
            description={d?.description || "N/A"}
            col={gridType !== 1}
            noSave
            rightAction={
              fromRecommendations && isTrue(d?.is_saved) ? null : (
                <Button
                  bordered
                  color={isTrue(d?.is_saved) ? "green" : "blue"}
                  height="25px"
                  classes="fs-12 minh-25"
                  onClick={() => handleSave(d?.id, d?.is_saved)}
                >
                  {isTrue(d?.is_saved)
                    ? t("Saved", "common")
                    : t("Save", "common")}
                </Button>
              )
            }
            iconsAndValuesList={getIconValues(d, t)}
            type={isSkills ? COMPONENT_TYPES.SKILL : COMPONENT_TYPES.CAREER}
            id={d?.id}
            fixedColHeight
            bottomContent={
              <div className={`flex mt-2 mb-1`}>
                <ChipsPreview
                  items={d?.related_skills || []}
                  limit={gridType === 1 ? 3 : 2}
                />
              </div>
            }
          />
        </Grid>
      ))}
      {fromRecommendations && (
        <div className="flex w-100p justify-center mt-2">
          <p className="link underline transform-none fw-600" onClick={showAll}>
            {isSkills ? t("Browse All Skills") : t("Browse All Careers")}
          </p>
        </div>
      )}
    </ListingContainer>
  );
};

export default CareersListing;

export const getIconValues = (item, t = (d) => d) =>
  [
    item?.related_challenges_count > 0 && {
      img: challenge,
      value: `${item?.related_challenges_count} ${t("Related Challenges")}`,
    },
    item?.related_labs_count > 0 && {
      img: lab,
      value: `${item?.related_labs_count} ${t("Related Labs")}`,
    },
    item?.related_resources_count > 0 && {
      img: resource,
      value: `${item?.related_resources_count} ${t("Related Resources")}`,
    },
    item?.skills?.length > 0 ||
      (item?.related_skills?.length > 0 && {
        img: skillImg,
        value: `${item?.skills?.length ?? item?.related_skills?.length} ${t(
          "Related Skills"
        )}`,
      }),
  ].filter(Boolean);
